import React, { FC, memo, useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import TableCommonHook from '@op/components/TableCommonHook';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { ISkillVersion } from '../../../types/skillDetail';
import { useSkillVersionsData } from '../../../hooks/useSkillVersionsData';
import SkillDetailEmptyState from '../SkillDetailEmptyState';
import './index.scss';

/**
 * 版本历史 Tab 内容 Props
 */
export interface ISkillVersionSectionProps {
  /** 当前 Skill 唯一标识 */
  skillId: string;
  /** 是否启用数据请求 */
  enabled: boolean;
}

const DEFAULT_SORT: SortBy<string> = {
  by: 'update_time',
  order: 'desc',
};

const PREFIX = 'dba-skill-version-section';

/**
 * 版本历史 Tab 内容
 */
const SkillVersionSection: FC<ISkillVersionSectionProps> = ({
  skillId,
  enabled,
}) => {
  const { formatMessage, locale } = useIntl();
  const isZh = locale === 'zh-CN';
  const {
    versions,
    total,
    loading,
    pageInfo,
    setPageInfo,
    sortInfo,
    handleSortChange,
    error,
  } = useSkillVersionsData({ skillId, enabled });

  /** 当前版本 = 当前页中 update_time 最新的那条（不受排序方向影响） */
  const currentVersionId = useMemo(() => {
    if (pageInfo.page !== 1 || versions.length === 0) {
      return undefined;
    }
    return versions.reduce((latest, current) => {
      const latestTime = new Date(latest.update_time || latest.create_time || 0).getTime();
      const currentTime = new Date(current.update_time || current.create_time || 0).getTime();
      return currentTime > latestTime ? current : latest;
    }).id;
  }, [pageInfo.page, versions]);

  const columns = useMemo(
    () => [
      {
        key: 'version',
        header: formatMessage({ id: 'dba_skill_detail_version_col_version' }),
        width: 160,
        render: (record: ISkillVersion) => (
          <div className={`${PREFIX}__version-cell`}>
            <span className={`${PREFIX}__version-text`}>
              {`v${record.version}`}
            </span>
            {currentVersionId === record.id && (
              <span className={`${PREFIX}__current-tag`}>
                {formatMessage({ id: 'dba_skill_detail_version_current' })}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'update_history',
        header: formatMessage({ id: 'dba_skill_detail_version_col_info' }),
        width: 360,
        render: (record: ISkillVersion) =>
          isZh ? record.update_history?.trim() : record.update_history_en?.trim()
          || '-',
      },
      {
        key: 'update_time',
        header: formatMessage({ id: 'dba_skill_detail_version_col_time' }),
        width: 150,
        render: (record: ISkillVersion) =>
          record.update_time || record.create_time || '-',
      },
    ],
    [currentVersionId, formatMessage],
  );

  const handleSortInfoChange = useCallback(
    (sort: SortBy<string>) => {
      handleSortChange(sort);
    },
    [handleSortChange],
  );

  return (
    <div className={PREFIX}>
      <TableCommonHook
        records={loading ? [] : versions}
        recordKey="id"
        columns={columns}
        loading={loading}
        total={total}
        pageInfo={pageInfo}
        setPageInfo={setPageInfo}
        sortType="server"
        sortInfo={[sortInfo, handleSortInfoChange]}
        sortColumns={['update_time']}
        defaultSort={DEFAULT_SORT}
        tableSticky
        bordered="all"
        minWidth="auto"
        emptyProps={{
          hasImg: false,
          hasTitle: false,
          hasDesc: true,
          emptyDescText: (
            <SkillDetailEmptyState
              message={formatMessage({
                id: error ? 'common_loading_failed' : 'dba_skill_detail_version_empty',
              })}
            />
          ),
        }}
      />
    </div>
  );
};

export default memo(SkillVersionSection);
